import { type KeyboardEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Avatar,
	Button,
	CloseIcon,
	IconButton,
	Label,
	Select,
	TextArea,
	UndoIcon,
	tokens,
} from '@treams/treams-ui';

import { type RetentionRiskLevel } from '../retentionRisk';
import { movedFromPrevious } from '../talentReviewSidebarOrder';
import TalentReviewRetentionRiskField from './TalentReviewRetentionRiskField';
import TalentReviewRetentionRiskMark from './TalentReviewRetentionRiskMark';
import TalentReviewSidebarStatus, { type SidebarStatusLine } from './TalentReviewSidebarStatus';
import TalentReviewSidebarSuccessors from './TalentReviewSidebarSuccessors';

export type TalentReviewSidebarCardMode = 'edit' | 'display';

const Body = styled(Box)`
	position: relative;
	width: 100%;
	box-sizing: border-box;
`;

const CloseButton = styled(IconButton)`
	position: absolute;
	top: ${tokens.padding.control}px;
	right: ${tokens.padding.control}px;
`;

const Header = styled(Flex)`
	align-items: center;
	gap: ${tokens.gap.list}px;
	min-width: 0;
	/* Keep the name clear of the close button. */
	padding-right: 32px;
`;

const Name = styled.div`
	color: ${tokens.text.primary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	font-weight: ${tokens.typography.weights.semibold};
	line-height: ${tokens.typography.lineHeights.body};
`;

const CellLabel = styled.div`
	color: ${tokens.text.primary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	line-height: ${tokens.typography.lineHeights.body};
`;

const Quote = styled.div`
	border-left: 3px solid ${tokens.border.default};
	padding-left: ${tokens.padding.control}px;
	color: ${tokens.text.primary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	line-height: ${tokens.typography.lineHeights.body};
	white-space: pre-wrap;
	word-break: break-word;

	& + & {
		margin-top: ${tokens.gap.element}px;
	}
`;

/**
 * The card that opens in the sidebar for the selected person. `edit` is the
 * live card (position, motivation, score, next); `display` is the stored
 * review on a readonly grid (snapshot, past day).
 * @param props component props
 * @param props.placement the selected person's placement
 * @param props.template the grid template, for the cell labels
 * @param props.mode which variant to render
 * @param props.movedThisSession shows the undo button
 * @param props.last the last person in the list: "next" only closes the card
 * @param props.outdated colours the ring
 * @param props.statusLines the same statuses as the closed row, under the name
 * @param props.hasScores whether the score select is enabled for the workspace
 * @param props.scoreOptions the score select options
 * @param props.focusKey bump to focus the textarea again when it is empty
 * @param props.onChangeMotivation fires per keystroke; saving is debounced upstream
 * @param props.onChangeScore fires with the new score
 * @param props.onChangeRetentionRisk fires with the new risk, or null when cleared
 * @param props.onChangePosition move the person to another cell
 * @param props.onNext "next": flush the motivation and select the next person
 * @param props.onUndo move the person back to where they came from
 * @param props.onClose close the card (deselect the person)
 */
export default function TalentReviewSidebarCard(props: {
	placement: graphql.TalentReviewGridPlacement;
	template: graphql.TalentReviewTemplate;
	mode: TalentReviewSidebarCardMode;
	movedThisSession?: boolean;
	last?: boolean;
	outdated?: boolean;
	statusLines: SidebarStatusLine[];
	hasScores?: boolean;
	scoreOptions: { title: string; value: graphql.TalentReviewPlacementScore }[];
	focusKey: number;
	onChangeMotivation: (motivation: string) => void;
	onChangeScore: (score: graphql.TalentReviewPlacementScore | null) => void;
	onChangeRetentionRisk: (retentionRisk: RetentionRiskLevel | null) => void;
	onChangePosition: (row: number, column: number) => void;
	onNext: () => void;
	onUndo: () => void;
	onClose: () => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const rootRef = useRef<HTMLDivElement>(null);

	const { placement, template } = props;
	const { user } = placement;
	const name = `${user.firstName} ${user.lastName}`;

	// Cells may have an empty label; fall back to the axis ticks.
	const cellLabel = (row: number, column: number) => {
		const label = localize(template.cells[row]?.[column]?.label)?.text;
		if (label) {
			return label;
		}

		const x = localize(template.xAxis.ticks[column])?.text ?? '';
		const y = localize(template.yAxis.ticks[row])?.text ?? '';
		return `${x} · ${y}`;
	};

	const moved = movedFromPrevious(placement);
	const movedPlaceholder = moved || !!props.movedThisSession;
	const currentLabel = cellLabel(placement.row, placement.column);
	const positionValue = `${placement.row}-${placement.column}`;
	const positionOptions = template.cells
		.flatMap((cells, row) =>
			cells.map((_, column) => ({ title: cellLabel(row, column), value: `${row}-${column}` })),
		)
		.reverse();

	const handleChangePosition = (value?: string | number) => {
		if (value === undefined || value === positionValue) {
			return;
		}

		const [row, column] = String(value).split('-').map(Number);
		props.onChangePosition(row, column);
	};

	// Focus the textarea when there is nothing to edit yet (empty motivation,
	// including after a move that cleared it). Skip when a review already
	// exists, so opening someone does not land the caret in their text. In a
	// frame: react-beautiful-dnd hands focus back to the drag handle right
	// after a drop, which would otherwise win. Without scrolling: the card is
	// still sliding in from the right at this point, and the browser would
	// drag the sidebar along to reach it.
	const shouldFocus = props.mode === 'edit' && !placement.motivation?.trim();
	useEffect(() => {
		if (!shouldFocus) {
			return;
		}

		const frame = requestAnimationFrame(() => {
			rootRef.current?.querySelector('textarea')?.focus({ preventScroll: true });
		});
		return () => cancelAnimationFrame(frame);
	}, [shouldFocus, props.focusKey]);

	const handleTextAreaKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
		if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
			event.preventDefault();
			props.onNext();
		}
	};

	const scoreTitle = props.scoreOptions.find(({ value }) => value === placement.score)?.title;
	const scoreSelect = props.hasScores && (
		<Box mt={2}>
			<Label>{t('talentReviews.v2.sidebar.card.scoreLabel')}</Label>
			<Box mt={1}>
				<Select
					small
					placeholder={t('talentReviews.edit.motivations.score.placeholder')}
					selectedValue={placement.score ?? undefined}
					options={props.scoreOptions}
					onChange={(score) =>
						props.onChangeScore((score as graphql.TalentReviewPlacementScore) ?? null)
					}
				/>
			</Box>
		</Box>
	);

	return (
		<Box ref={rootRef} tabIndex={-1} data-user-id={user.id}>
			<Body p={3}>
				<CloseButton
					type="secondary"
					icon={<CloseIcon />}
					aria-label={t('talentReviews.v2.close')}
					onClick={props.onClose}
				/>
				{props.mode === 'edit' ? (
					<>
						<Header>
							<TalentReviewRetentionRiskMark risk={placement.retentionRisk}>
								<Avatar size={48} user={user} />
							</TalentReviewRetentionRiskMark>
							<Box minWidth={0} flex={1}>
								<Name>{name}</Name>
								<TalentReviewSidebarStatus lines={props.statusLines} />
							</Box>
						</Header>
						<Box mt={3}>
							<Label>{t('talentReviews.v2.sidebar.card.positionLabel')}</Label>
							<Box mt={1}>
								<Select
									small
									role="combobox"
									aria-label={t('talentReviews.v2.sidebar.card.positionLabel')}
									selectedValue={positionValue}
									options={positionOptions}
									onChange={handleChangePosition}
								/>
							</Box>
						</Box>
						<Box mt={2}>
							<Label>{t('talentReviews.v2.sidebar.card.motivationLabel')}</Label>
							<Box mt={1}>
								<TextArea
									minRows={4}
									maxRows={10}
									aria-label={t('talentReviews.v2.sidebar.card.motivationLabel')}
									placeholder={
										movedPlaceholder
											? t('talentReviews.v2.sidebar.card.motivationPlaceholderMoved', {
													cell: currentLabel,
												})
											: t('talentReviews.v2.sidebar.card.motivationPlaceholder', { name })
									}
									value={placement.motivation ?? ''}
									onChange={props.onChangeMotivation}
									onKeyDown={handleTextAreaKeyDown}
								/>
							</Box>
						</Box>
						{scoreSelect}
						<Box mt={2}>
							<TalentReviewRetentionRiskField
								value={placement.retentionRisk}
								onChange={props.onChangeRetentionRisk}
							/>
						</Box>
						<TalentReviewSidebarSuccessors user={user} />
						<Box mt={2}>
							<Button
								type="primary"
								width="100%"
								role="button"
								aria-label={t(
									props.last
										? 'talentReviews.v2.sidebar.card.done'
										: 'talentReviews.v2.sidebar.card.next',
								)}
								title={t(
									props.last
										? 'talentReviews.v2.sidebar.card.done'
										: 'talentReviews.v2.sidebar.card.next',
								)}
								onClick={props.onNext}
							/>
						</Box>
						{props.movedThisSession && (
							<Box mt={2}>
								<Button
									type="secondary"
									width="100%"
									role="button"
									aria-label={t('talentReviews.v2.sidebar.card.undo')}
									icon={<UndoIcon />}
									title={t('talentReviews.v2.sidebar.card.undo')}
									onClick={props.onUndo}
								/>
							</Box>
						)}
					</>
				) : (
					<>
						<Header>
							<TalentReviewRetentionRiskMark risk={placement.retentionRisk}>
								<Avatar.Ring tone={props.outdated ? 'warning' : undefined} width={3}>
									<Avatar size={48} user={user} />
								</Avatar.Ring>
							</TalentReviewRetentionRiskMark>
							<Box minWidth={0} flex={1}>
								<Name>{name}</Name>
								<TalentReviewSidebarStatus lines={props.statusLines} />
							</Box>
						</Header>
						<Box mt={3}>
							<CellLabel>{currentLabel}</CellLabel>
						</Box>
						<Box mt={2}>
							{props.hasScores && scoreTitle && (
								<Quote>{t('talentReviews.v2.sidebar.card.score', { score: scoreTitle })}</Quote>
							)}
							<Box my={2}>
								<TalentReviewRetentionRiskField
									readOnly
									value={placement.retentionRisk}
									onChange={props.onChangeRetentionRisk}
								/>
							</Box>
							<Label>{t('talentReviews.v2.sidebar.card.motivationLabel')}</Label>
							<Box mt={1}>
								<Quote>
									{placement.motivation?.trim() || t('talentReviews.v2.sidebar.card.noMotivation')}
								</Quote>
							</Box>
						</Box>
					</>
				)}
			</Body>
		</Box>
	);
}
